import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../Context/auth';
import MenuLateral from '../Components/MenuPrincipal/MenuLateral';
import UnidMedGrid from '../Components/UnMedGrid/UnidMedGrid';
import Pagination from '../Components/Pagination/Pagination';
import './UnidadeMedida.css';

function UnidadesMedida() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();
    const [unidades, setUnidades] = useState([]);
    const [unidadesFiltradas, setUnidadesFiltradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);

    // Estados da Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Verifica permissão de leitura
    useEffect(() => {
        if (appUserData && !appUserData['ler_unidades_medida']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    useEffect(() => {
        const fetchUnidadesMedida = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                // Busca todas (client-side filtering por enquanto, mantendo padrão)
                const response = await fetch(`${API_URL}/api/unidadesmedida`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error("Não foi possível carregar as unidades de medida.");
                const data = await response.json();
                setUnidades(data);
                setUnidadesFiltradas(data); // Inicializa filtradas com todas
            } catch (error) {
                console.error("Erro ao buscar unidades de medida: ", error);
                Swal.fire("Erro", error.message, "error");
            } finally {
                setLoading(false);
            }
        };
        fetchUnidadesMedida();
    }, [currentUser, trigger]);

    // Lógica de Filtro Client-side
    useEffect(() => {
        let resultado = unidades;

        if (pesquisa) {
            const term = pesquisa.toLowerCase();
            resultado = resultado.filter(item =>
                item.descricao.toLowerCase().includes(term) ||
                item.sigla.toLowerCase().includes(term)
            );
        }

        setUnidadesFiltradas(resultado);
        setCurrentPage(1); // Reset page on filter change
    }, [pesquisa, unidades]);



    const handleAtualizar = () => {
        setPesquisa('');
        setTrigger(Date.now()); // Re-fetch
    };

    // Lógica de Paginação (Slice)
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = unidadesFiltradas.slice(indexOfFirstItem, indexOfLastItem);

    async function deleteUnidadeMedida(id) {
        // Validation for Delete
        if (appUserData && appUserData['excluir_unidades_medida'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        Swal.fire({
            title: "Você tem certeza?",
            text: "Esta ação não poderá ser revertida!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#DC3545",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = await currentUser.getIdToken();
                    const API_URL = process.env.REACT_APP_API_URL;
                    const response = await fetch(`${API_URL}/api/unidadesmedida/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || "Erro ao deletar");
                    }
                    setTrigger(Date.now());
                    Swal.fire("Excluído!", "A unidade de medida foi deletada com sucesso.", "success");
                } catch (error) {
                    Swal.fire("Erro!", error.message, "error");
                }
            }
        });
    }

    return (
        <div className="unidade-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar">
                <div className="unidade-header">
                    <h1 className="unidade-title">
                        <i className="fas fa-ruler-combined text-primary me-2"></i>
                        Listagem de <span className="highlight">Unidades de Medida</span>
                    </h1>
                </div>

                <div className="unidade-content">
                    <div className="unidade-controls">
                        <Link
                            to="/app/novaunidademedida"
                            className="btn btn-primary-custom me-2"
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_unidades_medida'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i> Adicionar Unidade
                        </Link>
                        <div className="search-input-group">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por descrição ou sigla..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                            />
                        </div>

                        <button className="btn btn-icon-custom" onClick={handleAtualizar} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    <div className="unidade-grid-container">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        ) : (
                            <UnidMedGrid arrayUnidadesMedida={currentItems} clickDelete={deleteUnidadeMedida} userPermissions={appUserData} />
                        )}
                    </div>
                </div>
            </div>

            <Pagination
                totalItems={unidadesFiltradas.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
            />
        </div>
    );
}

export default UnidadesMedida;